import React from "react";
import { FadeIn } from "components/shared/FadeIn";
import type { Degree } from "types";

interface Props {
  degree: Degree;
}

const getInstitutionAbbr = (title?: string) => {
  if (!title) return "";
  if (title.includes("Grand Valley")) return "GVSU";
  if (title.includes("GCTU")) return "GCTU";
  return title
    .split(" ")
    .map((p) => p[0])
    .slice(0, 3)
    .join("")
    .toUpperCase();
};

const DegreeCard: React.FC<Props> = ({ degree }) => {
  const abbr = getInstitutionAbbr(degree.title);

  return (
    <FadeIn duration={800}>
      <div className="group relative border-b border-border py-8 md:py-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {/* Institution */}
          <div className="md:col-span-3">
            <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 border border-border bg-bg-surface rounded text-text-primary font-display font-bold text-lg md:text-xl">
              {abbr || "—"}
            </div>
          </div>

          {/* Content */}
          <div className="md:col-span-9 space-y-4">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
              <div>
                <h3 className="text-xl md:text-2xl font-display font-bold tracking-tight text-text-primary">
                  {degree.title}
                </h3>
                <p className="text-sm font-medium text-accent mt-1">
                  {degree.subtitle}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1 text-xs font-mono text-text-muted">
                  <svg
                    className="w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {degree.location}
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-mono text-text-muted">
                  <svg
                    className="w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {degree.duration}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              {degree.descriptions?.map((sentence: string, idx: number) => (
                <p
                  key={idx}
                  className="text-sm leading-relaxed text-text-secondary"
                >
                  {sentence}
                </p>
              ))}
            </div>

            <a
              href={degree.website_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-mono text-accent hover:underline transition-colors"
            >
              Visit Website
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </FadeIn>
  );
};

export default React.memo(DegreeCard);
